<template>
  <div class="vue-audio-player">
    <div class="vue-audio-player__btn-wrap">
      <play-pause-button
        class="mr-2"
        :color="themeColor"
        :playing="isPlaying"
        @play="play"
        @pause="pause"
      />

      <div v-show="showProgressBar" class="vue-audio-player__time">
        {{ currentTimeFormatted }} / {{ durationFormatted }}
      </div>

      <div
        ref="audioProgressContainer"
        class="audio-player-progress-container mr-3 flex-grow-1"
        :style="{
          cursor: disabledProgressClick ? 'auto' : 'pointer',
        }"
        @click.stop="handleClickProgressWrap"
      >
        <div
          v-show="showProgressBar"
          ref="audioProgressWrap"
          class="vue-audio-player__progress-wrap"
        >
          <div
            ref="audioProgress"
            class="vue-audio-player__progress"
            :style="{
              backgroundColor: themeColor,
            }"
          ></div>
          <div
            ref="audioProgressPoint"
            class="vue-audio-player__progress-point"
            :style="{
              backgroundColor: themeColor,
              cursor: disabledProgressDrag ? 'auto' : 'pointer',
            }"
          ></div>
        </div>
      </div>

      <volume-button
        v-if="showVolumeButton"
        v-model="volume"
        class="mr-2"
        :color="themeColor"
      />

      <action-select
        :playback-rate.sync="playbackRate"
        :color="themeColor"
        :playback-rates="playbackRates"
        :src="src"
      />

      <div v-show="isShowErrorMessage" class="vue-audio-player__notice">
        {{ noticeMessage }}
      </div>
    </div>

    <audio
      ref="audio"
      class="vue-audio-player__audio"
      :src="src"
      v-bind="$attrs"
      @ended="onEnded"
      @durationchange="onDurationchange"
    ></audio>
  </div>
</template>

<script>
  import PlayPauseButton from './PlayPauseButton.vue'
  import VolumeButton from './VolumeButton.vue'
  import ActionSelect from '@/components/ui/audio-player/ActionSelect.vue'

  export default {
    name: 'AudioPlayer',
    components: { ActionSelect, PlayPauseButton, VolumeButton },
    inheritAttrs: false,
    props: {
      src: String,
      showVolumeButton: {
        default: true,
        type: Boolean,
      },
      showProgressBar: {
        default: true,
        type: Boolean,
      },
      beforePlay: {
        default: null,
        type: Function,
      },
      isLoop: {
        type: Boolean,
        default: true,
      },
      progressInterval: {
        default: 500,
        type: Number,
      },
      showPlayLoading: {
        type: Boolean,
        default: true,
      },
      playbackRates: {
        type: Array,
        default: () => [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2],
      },
      themeColor: {
        type: String,
        default: '#000',
      },
      disabledProgressDrag: {
        type: Boolean,
        default: false,
      },
      disabledProgressClick: {
        type: Boolean,
        default: false,
      },
    },
    emits: [
      'pause',
      'play-prev',
      'play-next',
      'timeupdate',
      'durationchange',
      'ended',
      'progress-start',
      'progress-end',
      'progress-move',
      'progress-click',
      'playing',
      'play',
      'play-error',
    ],
    data () {
      return {
        isIOS: /iPhone|iPad|iPod/i.test(navigator?.userAgent),
        isPlaying: false,
        isDragging: false,
        isDraggingVolume: false,
        isShowErrorMessage: false,
        isLoading: false,
        isShowRates: false,
        timer: null,
        noticeMessage: '',
        duration: 0,
        currentTime: '',
        playbackRate: 1,
        canProgressDrag: true,
        volume: 0,
        clickStartTime: 0,
      }
    },
    computed: {
      currentTimeFormatted () {
        return this.currentTime ? this.formatTime(this.currentTime) : '00:00'
      },
      durationFormatted () {
        return this.duration ? this.formatTime(this.duration) : '00:00'
      },
    },
    watch: {
      volume () {
        this.$refs.audio.volume = this.volume
      },
      playbackRate () {
        this.$refs.audio.playbackRate = this.playbackRate
      },
    },
    mounted () {
      this.volume = this.$refs.audio.volume
      this.playbackRate = this.$refs.audio.playbackRate

      this.$nextTick(() => {
        this.$refs.audioProgressContainer.addEventListener(
          'mousedown',
          this.handleProgressPanstart,
        )

        document.addEventListener('mousemove', this.handleProgressPanmove)
        document.addEventListener('mouseup', this.handleProgressPanend)

        this.$refs.audioProgressContainer.addEventListener(
          'touchstart',
          this.handleProgressPanstart,
        )

        document.addEventListener('touchmove', this.handleProgressPanmove)
        document.addEventListener('touchend', this.handleProgressPanend)

        this.$refs.audio.load()
      })
    },
    beforeUnmount () {
      this.pause()

      this.$refs.audioProgressContainer.removeEventListener(
        'mousedown',
        this.handleProgressPanstart,
      )

      document.removeEventListener('mousemove', this.handleProgressPanmove)
      document.removeEventListener('mouseup', this.handleProgressPanend)

      this.$refs.audioProgressContainer.removeEventListener(
        'touchstart',
        this.handleProgressPanstart,
      )

      document.removeEventListener('touchmove', this.handleProgressPanmove)
      document.removeEventListener('touchend', this.handleProgressPanend)
    },

    methods: {
      handleShowErrorMessage (opts = {}) {
        this.noticeMessage = opts.message
        this.isShowErrorMessage = true

        setTimeout(() => {
          this.isShowErrorMessage = false
        }, opts.duration || 3000)
      },

      onDurationchange (event) {
        this.duration = this.$refs.audio.duration

        this.$emit('durationchange', event)
      },

      formatTime (second) {
        let minute = 0

        minute = Math.floor(second / 60)
        second = Math.ceil(second % 60)

        if (second >= 60) {
          minute++
          second = 0
        }

        minute = String(minute)
        second = String(second)

        minute = minute.length === 1 ? '0' + minute : minute
        second = second.length === 1 ? '0' + second : second

        return minute + ':' + second
      },

      onEnded (event) {
        setTimeout(() => {
          this.pause()

          if (this.isLoop) {
          // this.playNext()
          }

          this.$emit('ended', event)
        }, this.progressInterval)
      },

      handleProgressPanstart (event) {
        this.clickStartTime = Date.now()
        this.canProgressDrag = false

        if (this.disabledProgressDrag) return

        this.isDragging = true

        this.$emit('progress-start', event)
      },

      handleProgressPanend (event) {
        if (this.disabledProgressDrag || !this.isDragging) return

        this.isDragging = false

        /**
         * Так как нет события удержание клавиши мыши, а только mousedown,
         * то в обработчике mouseup проверяем было ли это удержание или просто клик.
         * Сравниваем время в момент mousedown и в момент mouseup.
         * Тестами определено примерное время клика от 150 миллисекунд и ниже
         */
        if (this.clickStartTime && Date.now() - this.clickStartTime <= 150) {
          this.canProgressDrag = true
          this.handleClickProgressWrap(event)
          return
        }

        this.play({
          currentTime: this.currentTime,
        })

        this.$emit('progress-end', event)
      },

      handleProgressPanmove (event) {
        if (this.disabledProgressDrag || !this.isDragging) return

        this.pause()

        const pageX = event.pageX || event.touches[0].pageX
        const bcr = this.$refs.audioProgressPoint.getBoundingClientRect()

        const targetLeft = parseInt(
          getComputedStyle(this.$refs.audioProgressPoint).left,
        )

        let offsetLeft = targetLeft + (pageX - bcr.left)

        offsetLeft = Math.min(
          offsetLeft,
          this.$refs.audioProgressWrap.offsetWidth,
        )

        offsetLeft = Math.max(offsetLeft, 0)

        this.setPointPosition(offsetLeft)

        this.$refs.audioProgress.style.width = offsetLeft + 'px'

        this.currentTime =
          (offsetLeft / this.$refs.audioProgressWrap.offsetWidth) * this.duration

        this.$emit('progress-move', event)
      },
      handleClickProgressWrap (event) {
        if (this.disabledProgressClick || !this.canProgressDrag) {
          this.canProgressDrag = true
          return
        }

        const target = event.target
        const pageX = event.pageX || event.touches[0].pageX
        const bcr = target.getBoundingClientRect()
        const offsetLeft = pageX - bcr.left

        if (target === this.$refs.audioProgressPoint) {
          return
        }

        this.currentTime =
          (offsetLeft / this.$refs.audioProgressWrap.offsetWidth) * this.duration

        this.play({
          currentTime: this.currentTime,
        })

        this.setPointPosition(offsetLeft)

        this.$refs.audioProgress.style.width = offsetLeft + 'px'
        this.canProgressDrag = true

        this.$emit('progress-click', event)
      },

      setPointPosition (offsetLeft) {
        this.$refs.audioProgressPoint.style.left =
          offsetLeft - this.$refs.audioProgressPoint.offsetWidth / 2 + 'px'
      },

      playing () {
        if (this.isDragging) {
          return
        }

        const offsetLeft =
          (this.$refs.audio.currentTime / this.$refs.audio.duration) *
          this.$refs.audioProgressWrap.offsetWidth

        this.currentTime = this.$refs.audio.currentTime
        this.$refs.audioProgress.style.width = offsetLeft + 'px'
        this.setPointPosition(offsetLeft)
        this.$emit('playing')
      },

      play (opts = {}) {
        return new Promise((resolve, reject) => {
          this.isLoading = true

          const handlePlay = () => {
            this.$refs.audio
              .play()
              .then(() => {
                this.$nextTick(() => {
                  if (opts?.currentTime) {
                    this.$refs.audio.currentTime = opts.currentTime
                  }

                  if (this.timer) {
                    this.currentTime = this.$refs.audio.currentTime
                  } else {
                    this.timer = setInterval(this.playing, this.progressInterval)
                  }

                  this.isPlaying = true
                  this.isLoading = false
                  this.$refs.audio.playbackRate = this.playbackRate
                  this.$emit('play')

                  resolve(this.$refs.audio)
                })
              })
              .catch((data) => {
                this.handleShowErrorMessage({
                  message: data.message,
                })

                this.isLoading = false
                this.$emit('play-error', data)
                reject(data)
              })
          }

          if (this.isIOS) {
            this.$refs.audio.play()
            this.$refs.audio.pause()
          }

          if (this.beforePlay) {
            this.beforePlay((state) => {
              if (state !== false) {
                handlePlay()
              }
            })
            return
          }

          handlePlay()
        })
      },

      pause () {
        this.$refs.audio.pause()
        this.$nextTick(() => {
          this.clearTimer()
          this.isPlaying = false
          this.$emit('pause')
        })
      },

      clearTimer () {
        clearInterval(this.timer)
        this.timer = null
      },
    },
  }
</script>

<style lang="less" scoped>
.vue-audio-player {
  background-color: #f1f3f4;
  border-radius: 200px;
  padding: 12px 14px;
}

.vue-audio-player .vue-audio-player__btn-wrap {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
}

.vue-audio-player__notice {
  position: absolute;
  bottom: -15px;
  color: rgb(189, 178, 178);
  border-radius: 4px;
  font-size: 12px;
}

.vue-audio-player .vue-audio-player__progress-wrap {
  position: relative;
  background: #ddd;
  height: 4px;
  border-radius: 3px;
  touch-action: none;
  user-select: none;
  -webkit-user-drag: none;
  outline: none;
  -webkit-tap-highlight-color: transparent;
}

.vue-audio-player .vue-audio-player__progress {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 0;
  border-radius: 3px;
}

.vue-audio-player .vue-audio-player__progress-point {
  position: absolute;
  width: 12px;
  height: 12px;
  top: -4px;
  border-radius: 50%;
  opacity: 0;
  transition: opacity 0.3s;
}

.vue-audio-player .vue-audio-player__time {
  font-size: 14px;
  color: #000;
  margin-right: 16px;
}

.vue-audio-player .vue-audio-player__audio {
  display: block;
  margin: 0 auto;
}

.audio-player {
  &-progress-container {
    padding: 18px 0;
    touch-action: none;
    user-select: none;
    -webkit-user-drag: none;
    outline: none;
    -webkit-tap-highlight-color: transparent;

    &:hover .vue-audio-player__progress-point {
      opacity: 1;
    }
  }
}
</style>
